import type React from "react";
import { AlertCircle } from "lucide-react";

const LIMITED_REVIEW_HIDDEN_CARD_TITLES = new Set([
  "Tailoring Questions",
  "RoMMs",
  "Internal Controls",
  "Deficiencies",
]);

export type ExecutionLandingCategory = {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
};

function misstatementIdFromSubstantiveId(substantiveId: string): string {
  if (substantiveId.endsWith("-substantive")) {
    return `${substantiveId.slice(0, -"-substantive".length)}-misstatements`;
  }
  return substantiveId.replace(/substantive$/i, "misstatements");
}

function deriveMisstatementStub(
  categories: ExecutionLandingCategory[],
): ExecutionLandingCategory {
  const substantive = categories.find((c) => c.title === "Substantive Procedures");
  const id = substantive
    ? misstatementIdFromSubstantiveId(substantive.id)
    : "misstatements";
  return {
    id,
    title: "Misstatements",
    description: "View misstatements linked to this workspace.",
    icon: AlertCircle,
    accent: "from-orange-500/20 to-amber-500/20",
  };
}

/** When engagement is Limited Review, Execution workspaces only show substantive + misstatements (+ documents). */
export function applyLimitedReviewExecutionCardFilter<T extends ExecutionLandingCategory>(
  categories: T[],
  isLimitedReview: boolean,
): T[] {
  if (!isLimitedReview) return categories;
  const filtered = categories.filter((c) => !LIMITED_REVIEW_HIDDEN_CARD_TITLES.has(c.title));
  if (filtered.some((c) => c.title === "Misstatements")) {
    return filtered;
  }
  const stub = deriveMisstatementStub(categories) as unknown as T;
  return [...filtered, stub];
}

const LIMITED_REVIEW_HIDDEN_LIBRARY_IDS = new Set([
  "risk-library",
  "control-library",
  "rcm-library",
  "deficiency-library",
]);

/** Libraries (left nav + Libraries page): omit audit-only entries for Limited Review. */
export function isLibraryHiddenForLimitedReview(
  libraryId: string,
  isLimitedReviewExecution: boolean,
): boolean {
  if (!isLimitedReviewExecution) return false;
  return LIMITED_REVIEW_HIDDEN_LIBRARY_IDS.has(libraryId);
}
